import { ColumnLayout } from "@mittwald/flow-components/dist/components/ColumnLayout";
import { ContentBox } from "@mittwald/flow-components/dist/components/ContentBox";
import { useForm } from "@mittwald/flow-components/dist/components/Form";
import { Wizard } from "@mittwald/flow-components/dist/components/Wizard";
import useWizardState from "@mittwald/flow-components/dist/components/Wizard/hooks/useWizardState";
import { useAnimationController } from "@mittwald/flow-components/dist/hooks/useAnimationController";
import { useValueController } from "@mittwald/flow-components/dist/hooks/useValueController";
import { useOnChange } from "@mittwald/flow-lib/dist/hooks/useOnChange";
import useSelector from "@mittwald/flow-lib/dist/hooks/useSelector";
import { useUnpackedValue } from "@mittwald/flow-lib/dist/hooks/useUnpackedValue";
import invariant from "invariant";
import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import z from "zod";
import {
  Article,
  ArticleFactory,
  StorageArticleModifier,
} from "../../../model/article";
import VServerArticle from "../../../model/article/VServerArticle";
import type { CustomerUpdateInputs } from "../../../model/customer/Customer";
import Customer from "../../../model/customer/Customer";
import { CustomerList } from "../../../model/customer/CustomerList";
import type {
  InvoiceSettingsInputs,
  InvoiceSettingsPaymentInput,
} from "../../../model/customer/InvoiceSettings";
import { InvoiceSettings } from "../../../model/customer/InvoiceSettings";
import Bytes from "../../../model/misc/Bytes";
import type { RegistrationInputs } from "../../../model/signup/Signup";
import { Signup } from "../../../model/signup/Signup";
import CustomerListUI from "../../../model/ui/customer/CustomerListUI";
import ContractPartnerStep from "../../../pages/app/server/modals/OrderServerWizard/components/ContractPartnerStep";
import { CustomerStep } from "../../../pages/app/server/modals/OrderServerWizard/components/CustomerStep";
import OverviewStep from "../../../pages/app/server/modals/OrderServerWizard/components/OverviewStep";
import PaymentStep from "../../../pages/app/server/modals/OrderServerWizard/components/PaymentStep";
import { RecipientStep } from "../../../pages/app/server/modals/OrderServerWizard/components/RecipientStep";
import type { CustomerStepFields } from "../../../pages/app/server/types";
import sessionStore from "../../../store/session";
import TrackingAction from "../../components/tracking/TrackingAction";
import type { WidgetTrackingTypes } from "../../components/tracking/types";
import { withValidatedProps } from "../../withValidatedProps";
import { OrderComplete } from "../../components/orderComplete/OrderComplete";
import { MfaStep } from "./components/MfaStep";
import { OrderOverview } from "./components/OrderOverview";
import RegistrationOrLoginStep from "./components/RegistrationOrLoginStep";
import { VerificationStep } from "./components/VerificationStep";
import { orderVServerUrlParam } from "./orderVServerUrlParam";

export default withValidatedProps(
  z.object({
    orderCompleteWidgetUrl: z.string(),
  }),
  () => {
    const wizardState = useWizardState();

    const {
      articleId,
      storageSizeInGiB,
      freeTrial,
      transactionId,
      promotion,
      promotionDays,
    } = orderVServerUrlParam.decodeFromParam("p");

    const orderTrackingAction = new TrackingAction(
      transactionId,
    ).ofType<WidgetTrackingTypes>();

    /* order vserver */

    const storageSize = Bytes.of(storageSizeInGiB, "GiB");
    const selectedArticle = ArticleFactory.useLoadById(articleId);
    Article.assertType(selectedArticle, VServerArticle);

    const totalStorage = useUnpackedValue(storageSize);
    const inclusiveStorage = selectedArticle.baseStorageAttribute.bytes;
    const additionalStorage = totalStorage.subtract(inclusiveStorage);
    const mfaEnabled = useSelector(() => !!sessionStore.mfaEnabled);

    const storageModifier = selectedArticle.getModifier(
      StorageArticleModifier.name,
    );

    const [orderCompleted, setOrderCompleted] = useState<boolean>(false);

    const orderVServerForm = useForm<{ customerId: string }>({
      onSubmit: async (values) => {
        const orderId = await VServerArticle.order({
          customerId: values.customerId,
          storageSizeInBytes: storageSize.bytes,
          description: "Mein vServer",
          articleId,
          useFreeTrial: freeTrial,
          promotionCode: promotion,
          recommendationCode: Cookies.get("recommendationCode"),
        });

        const orderPreview = await selectedArticle.getOrderPreview({
          storageSize,
        });

        const products = [
          {
            sku: selectedArticle.id,
            name: selectedArticle.name.toLowerCase(),
            category: "article",
            price: orderPreview.machine.toMoneyValueInput(),
            quantity: 1,
          },
        ];

        if (storageModifier && additionalStorage.bytes > 0) {
          products.push({
            sku: storageModifier.articleId,
            name: additionalStorage.pretty(),
            category: "articleModifier",
            price: orderPreview.storage.toMoneyValueInput(),
            quantity: 1,
          });
        }

        orderTrackingAction.triggerAnalytics("checkout", {
          orderId,
          isTesting: freeTrial,
          transactionTotal: orderPreview.total.toMoneyValueInput(),
          transactionProducts: products,
        });

        setOrderCompleted(true);
      },
    });

    const watchedCustomerId = orderVServerForm.watch("customerId");

    /* register or login */
    const [userId, setUserId] = useState<string>("");
    const rejectionAnimation = useAnimationController();

    const registrationOrLoginForm = useForm<
      RegistrationInputs & { type: "registration" | "login" }
    >({
      defaultValues: { person: { title: "other" }, type: "registration" },
      onSubmit: async (values) => {
        if (values.type === "registration") {
          const userId = await Signup.register(values);
          setUserId(userId);
        } else {
          return await Signup.login(values, rejectionAnimation);
        }
      },
    });

    useOnChange(() => {
      wizardState.gotoStep("mfa");
    }, [mfaEnabled]);

    const [watchedEmail, watchedPassword, watchedRegistrationType] =
      registrationOrLoginForm.watch(["email", "password", "type"]);

    const isRegistration = watchedRegistrationType === "registration";

    /* select or create customer */

    const customerList = CustomerList.tryUseAll();
    const customersAllowedToOrder = customerList.getCustomersAllowedToOrder();
    const customerSelectOptions =
      CustomerListUI.of(customerList).useSelectOptions();
    const existingCustomersAvailable = customerSelectOptions.length > 0;
    const customerModeController = useValueController(
      existingCustomersAvailable ? "existing" : "new",
    );
    const customerMode = customerModeController.watch();

    const customerForm = useForm<CustomerStepFields>({
      showSuccessFeedback: false,
      translationKey: "addCustomer",
      defaultValues: {
        id: "",
        name: "",
      },
      onSubmit: async (values) => {
        const { name, id } = values;

        if (customerModeController.value.current === "new") {
          invariant(name !== undefined, "Invalid state");

          const response = await Customer.createNew({ name });
          customerModeController.updateValue("existing");
          orderVServerForm.setValue("customerId", response.id);
        } else {
          invariant(id !== undefined, "Invalid state");
          orderVServerForm.setValue("customerId", id);
        }
      },
    });

    const selectedCustomerId = customerForm.watch("id");
    const selectedCustomer = customersAllowedToOrder.find(
      (c) => c.id === selectedCustomerId,
    );

    const hideContractPartnerStep = useMemo(() => {
      const selectedCustomerHasOwner = !!selectedCustomer?.contact;
      return customerMode === "existing" && selectedCustomerHasOwner;
    }, [selectedCustomerId, customerMode]);

    useEffect(() => {
      customerForm.setValue("id", watchedCustomerId);
    }, [watchedCustomerId]);

    /* update contract partner or create customer with contract partner */

    const contractPartnerForm = useForm<CustomerUpdateInputs>({
      defaultValues: {
        owner: { emailAddress: watchedEmail, salutation: "other" },
      },
      onSubmit: async (values) => {
        if (watchedCustomerId) {
          const customer = await Customer.loadById(watchedCustomerId);
          await customer.update(values);
        } else {
          const newCustomer = await Customer.createNew({
            ...values,
            name: "Meine Organisation",
          });
          orderVServerForm.setValue("customerId", newCustomer.id);
        }
      },
    });

    /* update payment method */

    const paymentForm = useForm<
      InvoiceSettingsInputs & {
        otherRecipient: boolean;
      }
    >({
      defaultValues: {
        paymentSettings: {
          method: "invoice",
          iban: "",
        },
        invoicePeriod: 1,
        invoiceRecipient: "contractPartner",
      },
      onSubmit: async (values) => {
        await InvoiceSettings.update(values, watchedCustomerId);
      },
    });

    const otherRecipient = paymentForm.watch("otherRecipient");
    const [
      watchedPaymentMethod,
      watchedPaymentIban,
      watchedPaymentBic,
      watchedPaymentAccountHolder,
      watchedPaymentConfirmDebit,
    ] = paymentForm.watch([
      "paymentSettings.method",
      "paymentSettings.iban",
      "paymentSettings.bic",
      "paymentSettings.accountHolder",
      "paymentSettings.confirmDebit",
    ]);
    const watchedPaymentData: InvoiceSettingsPaymentInput = {
      iban: watchedPaymentIban,
      bic: watchedPaymentBic,
      accountHolder: watchedPaymentAccountHolder,
      method: watchedPaymentMethod,
      confirmDebit: watchedPaymentConfirmDebit,
    };

    const registrationStep = (
      <RegistrationOrLoginStep
        form={registrationOrLoginForm}
        rejectionAnimation={rejectionAnimation}
        trackingId={transactionId}
      />
    );

    const verificationStep = (
      <VerificationStep
        email={watchedEmail}
        password={watchedPassword}
        rejectionAnimation={rejectionAnimation}
        trackingId={transactionId}
        userId={userId}
        wizardState={wizardState}
      />
    );

    const mfaStep = (
      <MfaStep
        email={watchedEmail}
        password={watchedPassword}
        rejectionAnimation={rejectionAnimation}
        trackingId={transactionId}
        wizardState={wizardState}
      />
    );

    const customerStep = (
      <CustomerStep
        customerModeController={customerModeController}
        form={customerForm}
        noReturn
        trackingId={transactionId}
      />
    );

    const contractPartnerStep = (
      <ContractPartnerStep
        form={contractPartnerForm}
        noReturn={isRegistration}
        trackingId={transactionId}
      />
    );

    const paymentStep = <PaymentStep form={paymentForm} />;

    const recipientStep = (
      <RecipientStep
        trackingId={transactionId}
        watchedCustomerId={watchedCustomerId}
        watchedPaymentData={watchedPaymentData}
      />
    );

    const overviewStep = (
      <OverviewStep
        customerId={watchedCustomerId}
        isWidget
        promotion={
          promotion && promotionDays
            ? { promoCode: promotion, promotionDays }
            : undefined
        }
        selectedArticle={selectedArticle}
        storageSize={storageSize}
        trackingId={transactionId}
      />
    );

    if (orderCompleted) {
      return <OrderComplete customerId={watchedCustomerId} />;
    }

    return (
      <ColumnLayout large={[2, 1]}>
        <ContentBox>
          <Wizard
            form={orderVServerForm}
            steps={[
              "registration",
              "verification",
              "mfa",
              "customer",
              "contractPartner",
              "payment",
              "recipient",
              "overview",
            ]}
            wizardState={wizardState}
          >
            {registrationStep}
            {isRegistration && verificationStep}
            {!isRegistration && mfaEnabled && mfaStep}
            {!isRegistration && customerStep}
            {!hideContractPartnerStep && contractPartnerStep}
            {!hideContractPartnerStep && paymentStep}
            {otherRecipient && recipientStep}
            {overviewStep}
          </Wizard>
        </ContentBox>
        <ContentBox>
          <OrderOverview
            freeTrial={freeTrial}
            promotion={
              promotion && promotionDays
                ? { promoCode: promotion, promotionDays }
                : undefined
            }
            selectedArticle={selectedArticle}
            storageSize={storageSize}
          />
        </ContentBox>
      </ColumnLayout>
    );
  },
);
